import {
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  FaCarBattery,
  FaExclamationTriangle,
  FaGasPump,
  FaTachometerAlt,
  FaTemperatureHigh,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

const HOURS = [
  { value: 1, label: "1 ora" },
  { value: 6, label: "6 ore" },
  { value: 24, label: "24 ore" },
  { value: 168, label: "7 giorni" },
];

function getConfig() {
  const token = localStorage.getItem("token");

  return {
    headers: token
      ? { Authorization: `Bearer ${token}` }
      : {},
  };
}

function show(value, unit = "", decimals = 0) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "—";
  }

  return `${number.toFixed(decimals)}${unit}`;
}

function formatRuntime(seconds) {
  const value = Number(seconds);

  if (!Number.isFinite(value)) {
    return "—";
  }

  const h = Math.floor(value / 3600);
  const m = Math.floor((value % 3600) / 60);

  return h > 0
    ? `${h} h ${m} min`
    : `${m} min`;
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("it-IT");
}

function Sparkline({
  samples,
  field,
  label,
  unit,
}) {
  const points = useMemo(() => {
    const values = samples
      .map((item) => item?.[field])
      .filter(
        (value) =>
          value !== null &&
          value !== undefined &&
          value !== ""
      )
      .map(Number)
      .filter(Number.isFinite);

    if (values.length < 2) {
      return null;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = Math.max(1, max - min);

    return {
      min,
      max,
      polyline: values
        .map((value, index) => {
          const x =
            (index / (values.length - 1)) *
            100;

          const y =
            100 -
            ((value - min) / range) *
              100;

          return `${x},${y}`;
        })
        .join(" "),
    };
  }, [samples, field]);

  return (
    <div className="flotta-obd-chart">
      <div className="flotta-obd-chart-head">
        <strong>{label}</strong>

        {points && (
          <span>
            {show(
              points.min,
              unit,
              field ===
                "controlModuleVoltage"
                ? 2
                : 0
            )}
            {" – "}
            {show(
              points.max,
              unit,
              field ===
                "controlModuleVoltage"
                ? 2
                : 0
            )}
          </span>
        )}
      </div>

      {!points ? (
        <div className="flotta-obd-chart-empty">
          Dati insufficienti
        </div>
      ) : (
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="flotta-obd-sparkline"
          aria-label={label}
        >
          <polyline points={points.polyline} />
        </svg>
      )}
    </div>
  );
}


function deriveObdAnalytics(samples) {
  const rows = (Array.isArray(samples) ? samples : [])
    .filter((row) => row && row.timestamp)
    .slice()
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() -
        new Date(b.timestamp).getTime()
    );

  let observedSeconds = 0;
  let engineOnSeconds = 0;
  let idleSeconds = 0;
  let movingSeconds = 0;
  let coldEngineSeconds = 0;
  let highLoadSeconds = 0;

  const rpmValues = [];
  const loadValues = [];
  const speedValues = [];
  const mafValues = [];
  const boostValues = [];

  for (const row of rows) {
    if (Number.isFinite(row.engineRpm)) {
      rpmValues.push(row.engineRpm);
    }
    if (Number.isFinite(row.engineLoadPct)) {
      loadValues.push(row.engineLoadPct);
    }
    if (Number.isFinite(row.vehicleSpeedKmh)) {
      speedValues.push(row.vehicleSpeedKmh);
    }
    if (Number.isFinite(row.mafGps)) {
      mafValues.push(row.mafGps);
    }
    if (
      Number.isFinite(row.intakeMapKpa) &&
      Number.isFinite(row.barometricPressureKpa)
    ) {
      boostValues.push(
        row.intakeMapKpa - row.barometricPressureKpa
      );
    }
  }

  for (let i = 1; i < rows.length; i += 1) {
    const prev = rows[i - 1];
    const current = rows[i];

    const t1 = new Date(prev.timestamp).getTime();
    const t2 = new Date(current.timestamp).getTime();
    const dt = (t2 - t1) / 1000;

    if (!Number.isFinite(dt) || dt <= 0 || dt > 180) {
      continue;
    }

    observedSeconds += dt;

    const rpm = Number(prev.engineRpm);
    const speed = Number(prev.vehicleSpeedKmh);
    const coolant = Number(prev.coolantTempC);
    const load = Number(prev.engineLoadPct);

    if (Number.isFinite(rpm) && rpm > 0) {
      engineOnSeconds += dt;

      if (
        Number.isFinite(speed) &&
        speed <= 1
      ) {
        idleSeconds += dt;
      }

      if (
        Number.isFinite(coolant) &&
        coolant < 75
      ) {
        coldEngineSeconds += dt;
      }

      if (
        Number.isFinite(load) &&
        load >= 70
      ) {
        highLoadSeconds += dt;
      }
    }

    if (
      Number.isFinite(speed) &&
      speed > 1
    ) {
      movingSeconds += dt;
    }
  }

  const avg = (values) => {
    if (!values.length) return null;
    return (
      values.reduce((sum, value) => sum + value, 0) /
      values.length
    );
  };

  const max = (values) =>
    values.length ? Math.max(...values) : null;

  const pct = (part, total) =>
    total > 0 ? (part / total) * 100 : null;

  return {
    observedSeconds,
    engineOnSeconds,
    idleSeconds,
    movingSeconds,
    idlePct: pct(idleSeconds, engineOnSeconds),
    coldEngineSeconds,
    coldEnginePct: pct(
      coldEngineSeconds,
      engineOnSeconds
    ),
    highLoadSeconds,
    highLoadPct: pct(
      highLoadSeconds,
      engineOnSeconds
    ),
    averageRpm: avg(rpmValues),
    averageLoadPct: avg(loadValues),
    averageSpeedKmh: avg(speedValues),
    averageMafGps: avg(mafValues),
    averageMapOverBaroKpa: avg(boostValues),
    maxMapOverBaroKpa: max(boostValues),
    quality: {
      sampleCount: rows.length,
      timedSeconds: observedSeconds,
      note:
        "Tempi calcolati solo tra campioni consecutivi con gap <= 180 s.",
    },
  };
}

function formatHoursFromSeconds(value) {
  if (!Number.isFinite(value)) return "?";
  if (value < 3600) {
    return `${Math.round(value / 60)} min`;
  }
  return `${(value / 3600).toFixed(1)} h`;
}

export default function FlottaObd() {
  const [snapshot, setSnapshot] =
    useState(null);
  const [history, setHistory] =
    useState(null);
  const [hours, setHours] =
    useState(6);
  const [loading, setLoading] =
    useState(true);
  const [historyLoading, setHistoryLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const loadSnapshot = async ({
    spinner = false,
  } = {}) => {
    if (spinner) {
      setLoading(true);
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/flotta/obd`,
        getConfig()
      );

      setSnapshot(response.data);
      setError("");
    } catch (err) {
      console.error(
        "Errore diagnostica OBD:",
        err
      );
      setError(
        "Diagnostica OBD non disponibile"
      );
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (
    selectedHours
  ) => {
    setHistoryLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/flotta/obd/history`,
        {
          ...getConfig(),
          params: {
            hours: selectedHours,
          },
        }
      );

      setHistory(response.data);
    } catch (err) {
      console.error(
        "Errore storico OBD:",
        err
      );
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadSnapshot({ spinner: true });

    const timer = setInterval(
      () => loadSnapshot(),
      10000
    );

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadHistory(hours);
  }, [hours]);

  if (loading && !snapshot) {
    return (
      <div className="flotta-obd-loading">
        Caricamento diagnostica OBD...
      </div>
    );
  }

  const obd = snapshot?.obd;
  const status =
    snapshot?.status || {};
  const diagnostics =
    snapshot?.diagnostics || {};
  const statistics =
    history?.statistics || {};
  const samples = Array.isArray(
    history?.samples
  )
    ? history.samples
    : [];

  const analytics =
    history?.analytics ||
    deriveObdAnalytics(samples);

  const visualStatus =
    status.code === "disconnected"
      ? "stale"
      : status.code || "offline";

  return (
    <section className="flotta-obd-page">
      <div className="flotta-obd-title-row">
        <div>
          <h3>Diagnostica motore OBD-II</h3>
          <p>
            OBDLink LX → FMC920 → Traccar
          </p>
        </div>

        <div className="flotta-obd-actions">
          <span
            className={`flotta-obd-status ${visualStatus}`}
          >
            {status.label ||
              "OBD non disponibile"}
          </span>

          <button
            type="button"
            onClick={() => {
              loadSnapshot({
                spinner: true,
              });
              loadHistory(hours);
            }}
          >
            <FaSyncAlt /> Aggiorna
          </button>
        </div>
      </div>

      {status.code === "disconnected" && (
        <div className="flotta-obd-warning">
          FMC920 online ma nessuna telemetria
          OBD recente: verificare il collegamento
          OBDLink LX ↔ FMC920.
        </div>
      )}

      {error && (
        <div className="flotta-obd-warning">
          {error}
        </div>
      )}

      <div className="flotta-obd-meta">
        <div>
          <strong>Ultimo dato OBD</strong>
          <span>
            {formatDateTime(obd?.timestamp)}
          </span>
        </div>

        <div>
          <strong>Stato accensione</strong>
          <span>
            {snapshot?.ignition === true
              ? "ON"
              : snapshot?.ignition === false
                ? "OFF"
                : "—"}
          </span>
        </div>

        <div>
          <strong>AVL ricevuti</strong>
          <span>
            {obd?.availableAvlIds?.length
              ? obd.availableAvlIds.join(", ")
              : "—"}
          </span>
        </div>

        <div>
          <strong>Stato collegamento</strong>
          <span>
            {status.detail || "—"}
          </span>
        </div>
      </div>

      <div className="flotta-obd-main-grid">
        <div className="flotta-obd-metric">
          <FaTachometerAlt />
          <strong>Giri motore</strong>
          <span>
            {show(obd?.engineRpm, " rpm")}
          </span>
        </div>

        <div className="flotta-obd-metric">
          <FaTemperatureHigh />
          <strong>Refrigerante</strong>
          <span>
            {show(obd?.coolantTempC, " °C")}
          </span>
        </div>

        <div className="flotta-obd-metric">
          <FaGasPump />
          <strong>Carburante ECU RAW</strong>
          <span>
            {show(obd?.fuelLevelPct, " %")}
          </span>
        </div>

        <div className="flotta-obd-metric">
          <FaCarBattery />
          <strong>Tensione ECU</strong>
          <span>
            {show(
              obd?.controlModuleVoltage,
              " V",
              2
            )}
          </span>
        </div>
      </div>

      <div className="flotta-obd-detail-grid">
        <div>
          <strong>Carico motore</strong>
          <span>
            {show(obd?.engineLoadPct, " %")}
          </span>
        </div>

        <div>
          <strong>Velocità OBD</strong>
          <span>
            {show(
              obd?.vehicleSpeedKmh,
              " km/h"
            )}
          </span>
        </div>

        <div>
          <strong>MAP</strong>
          <span>
            {show(obd?.intakeMapKpa, " kPa")}
          </span>
        </div>

        <div>
          <strong>MAF</strong>
          <span>
            {show(obd?.mafGps, " g/s", 2)}
          </span>
        </div>

        <div>
          <strong>Pressione barometrica</strong>
          <span>
            {show(
              obd?.barometricPressureKpa,
              " kPa"
            )}
          </span>
        </div>

        <div>
          <strong>Tempo motore</strong>
          <span>
            {formatRuntime(
              obd?.runtimeSeconds
            )}
          </span>
        </div>

        <div>
          <strong>Distanza con MIL</strong>
          <span>
            {show(
              obd?.milDistanceKm,
              " km"
            )}
          </span>
        </div>

        <div>
          <strong>Km dall'ultimo reset DTC</strong>
          <span>
            {show(
              obd?.distanceSinceCodesClearKm,
              " km"
            )}
          </span>
        </div>

        <div>
          <strong>Campioni RAW storico</strong>
          <span>
            {history?.count ?? "—"}
          </span>
        </div>


        <div>
          <strong>Campioni grafico</strong>
          <span>
            {history?.chartCount ??
              history?.samples?.length ??
              "?"}
          </span>
        </div>
      </div>

      <div
        className={`flotta-obd-dtc ${
          diagnostics.dtcPresent
            ? "alert"
            : "ok"
        }`}
      >
        <FaExclamationTriangle />

        <div>
          <strong>Diagnostica DTC</strong>
          <span>
            {diagnostics.dtcCount === null ||
            diagnostics.dtcCount ===
              undefined
              ? "Dato non disponibile"
              : diagnostics.dtcCount > 0
                ? `${diagnostics.dtcCount} DTC segnalati`
                : "Nessun DTC segnalato"}
          </span>
          <small>
            AVL 30 trasmette il numero dei
            DTC, non i codici Pxxxx.
          </small>
        </div>
      </div>

      <div className="flotta-obd-history-head">
        <div>
          <strong>Analisi utilizzo</strong>
          <span>
            Indicatori derivati dai campioni OBD del periodo selezionato
          </span>
        </div>
      </div>

      <div className="flotta-obd-stats">
        <div>
          <strong>Tempo motore osservato</strong>
          <span>
            {formatHoursFromSeconds(
              analytics.engineOnSeconds
            )}
          </span>
        </div>

        <div>
          <strong>Tempo al minimo</strong>
          <span>
            {formatHoursFromSeconds(
              analytics.idleSeconds
            )}
          </span>
        </div>

        <div>
          <strong>Minimo su motore</strong>
          <span>
            {show(analytics.idlePct, " %", 1)}
          </span>
        </div>

        <div>
          <strong>Motore freddo</strong>
          <span>
            {show(
              analytics.coldEnginePct,
              " %",
              1
            )}
          </span>
        </div>

        <div>
          <strong>Carico alto</strong>
          <span>
            {show(
              analytics.highLoadPct,
              " %",
              1
            )}
          </span>
        </div>

        <div>
          <strong>RPM medio</strong>
          <span>
            {show(
              analytics.averageRpm,
              " rpm",
              0
            )}
          </span>
        </div>

        <div>
          <strong>Velocit? OBD media</strong>
          <span>
            {show(
              analytics.averageSpeedKmh,
              " km/h",
              1
            )}
          </span>
        </div>

        <div>
          <strong>MAF medio</strong>
          <span>
            {show(
              analytics.averageMafGps,
              " g/s",
              2
            )}
          </span>
        </div>

        <div>
          <strong>MAP relativa media</strong>
          <span>
            {show(
              analytics.averageMapOverBaroKpa,
              " kPa",
              1
            )}
          </span>
        </div>

        <div>
          <strong>MAP relativa max</strong>
          <span>
            {show(
              analytics.maxMapOverBaroKpa,
              " kPa",
              1
            )}
          </span>
        </div>


        <div>
          <strong>Intervalli validi</strong>
          <span>
            {analytics.intervalsUsed ?? "?"}
          </span>
        </div>

        <div>
          <strong>Gap scartati</strong>
          <span>
            {analytics.gapsDiscarded ?? "?"}
          </span>
        </div>

        <div>
          <strong>Copertura calcolata</strong>
          <span>
            {formatHoursFromSeconds(
              analytics.timedSeconds ??
                analytics.observedSeconds
            )}
          </span>
        </div>


        <div>
          <strong>Fuel filtrati</strong>
          <span>
            {history?.fuelQuality?.rejectedSamples ??
              "?"}
          </span>
        </div>

        <div>
          <strong>Eventi fuel filtrati</strong>
          <span>
            {history?.fuelQuality?.rejectedRuns ??
              "?"}
          </span>
        </div>


        <div>
          <strong>Fuel sospetti non confermati</strong>
          <span>
            {history?.fuelQuality?.suspectSamples ??
              "?"}
          </span>
        </div>
      </div>

      <div className="flotta-obd-warning">
        <strong>Consumi e costi:</strong>{" "}
        il MAF ? disponibile, ma il Fiorino non espone Fuel Rate.
        Per evitare valori fuorvianti, L/h, L/100 km e costi verranno
        attivati nella fase di calibrazione con rifornimenti reali.
        I calcoli usano tutti i campioni RAW disponibili; i grafici sono
        ridotti a un massimo di 600 punti. I gap superiori a 180 secondi
        Il grafico carburante corregge solo zero interni confermati da
        letture coerenti ai due lati; un contesto stabile puo' confermare
        il falso zero anche oltre 180 s. Gli zero non confermabili, compresi
        quelli a bordo serie, non vengono interpolati: restano RAW e sono
        marcati sospetti. Il grafico filtrato li omette senza inventare valori.
        Nessun dato ECU RAW viene cancellato o sovrascritto.
      </div>

      <div className="flotta-obd-history-head">
        <div>
          <strong>Storico motore</strong>
          <span>
            Dati conservati da Traccar
          </span>
        </div>

        <div className="flotta-obd-periods">
          {HOURS.map((item) => (
            <button
              type="button"
              key={item.value}
              className={
                hours === item.value
                  ? "active"
                  : ""
              }
              onClick={() =>
                setHours(item.value)
              }
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {historyLoading ? (
        <div className="flotta-obd-loading">
          Caricamento storico...
        </div>
      ) : (
        <>
          <div className="flotta-obd-stats">
            <div>
              <strong>RPM max</strong>
              <span>
                {show(
                  statistics.maxRpm,
                  " rpm"
                )}
              </span>
            </div>

            <div>
              <strong>Temperatura max</strong>
              <span>
                {show(
                  statistics.maxCoolantTempC,
                  " °C"
                )}
              </span>
            </div>

            <div>
              <strong>Carico medio</strong>
              <span>
                {show(
                  statistics.averageEngineLoadPct,
                  " %",
                  1
                )}
              </span>
            </div>

            <div>
              <strong>MAF max</strong>
              <span>
                {show(
                  statistics.maxMafGps,
                  " g/s",
                  2
                )}
              </span>
            </div>

            <div>
              <strong>Tensione min</strong>
              <span>
                {show(
                  statistics.minControlModuleVoltage,
                  " V",
                  2
                )}
              </span>
            </div>

            <div>
              <strong>DTC massimo</strong>
              <span>
                {statistics.maxDtcCount ??
                  "—"}
              </span>
            </div>
          </div>

          <div className="flotta-obd-charts">
            <Sparkline
              samples={samples}
              field="engineRpm"
              label="RPM"
              unit=" rpm"
            />
            <Sparkline
              samples={samples}
              field="coolantTempC"
              label="Temperatura refrigerante"
              unit=" °C"
            />
            <Sparkline
              samples={samples}
              field="controlModuleVoltage"
              label="Tensione ECU"
              unit=" V"
            />
            <Sparkline
              samples={samples}
              field="fuelLevelFilteredPct"
              label="Carburante filtrato"
              unit=" %"
            />
          </div>
        </>
      )}
    </section>
  );
}
